import React, { useEffect, useState } from 'react';
import { supabase } from '../supabaseClient';
import { SubscriptionPlan, Subscription as BaseSubscription, PLAN_LIMITS } from '../services/subscriptionService';
import { User, Crown, Trash2, RefreshCw, AlertTriangle, CheckCircle, Search } from 'lucide-react';

// Extended Subscription type for admin dashboard
interface Subscription extends BaseSubscription {
  profiles?: {
    email?: string;
    full_name?: string;
    avatar_url?: string;
    stripe_customer_id?: string;
  };
}

interface AdminDashboardProps {
  isAdmin: boolean;
}

export const SubscriptionAdminDashboard: React.FC<AdminDashboardProps> = ({ isAdmin }) => {
  const [subscriptions, setSubscriptions] = useState<Subscription[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedSubscription, setSelectedSubscription] = useState<Subscription | null>(null);
  const [isUpdating, setIsUpdating] = useState(false);

  useEffect(() => {
    if (!isAdmin) return;
    
    fetchSubscriptions();
  }, [isAdmin]);

  const fetchSubscriptions = async () => {
    try {
      setLoading(true);
      
      const { data, error } = await supabase
        .from('subscriptions')
        .select(`
          *,
          profiles:user_id (
            email,
            full_name,
            avatar_url,
            stripe_customer_id
          )
        `)
        .order('created_at', { ascending: false })
        .limit(100);
      
      if (error) throw error;
      
      setSubscriptions(data || []);
    } catch (err) {
      console.error('Error fetching subscriptions:', err);
      setError('Failed to load subscriptions');
    } finally {
      setLoading(false);
    }
  };

  const deactivateSubscription = async (subscriptionId: string) => {
    if (!confirm('Are you sure you want to deactivate this subscription?')) return;
    
    try {
      setIsUpdating(true);
      
      const { error } = await supabase
        .from('subscriptions')
        .update({ 
          is_active: false,
          updated_at: new Date().toISOString()
        })
        .eq('id', subscriptionId);
      
      if (error) throw error;
      
      // Refresh the list
      await fetchSubscriptions();
      
      // Clear selection if the deactivated subscription was selected
      if (selectedSubscription?.id === subscriptionId) {
        setSelectedSubscription(null);
      }
    } catch (err) {
      console.error('Error deactivating subscription:', err);
      setError('Failed to deactivate subscription');
    } finally {
      setIsUpdating(false);
    }
  };

  const upgradeToPlan = async (userId: string, plan: SubscriptionPlan) => {
    if (!confirm(`Are you sure you want to upgrade this user to ${plan.toUpperCase()} plan?`)) return;
    
    try {
      setIsUpdating(true);
      
      // First deactivate any existing subscriptions
      const { error: deactivateError } = await supabase
        .from('subscriptions')
        .update({ 
          is_active: false,
          updated_at: new Date().toISOString()
        })
        .eq('user_id', userId)
        .eq('is_active', true);
      
      if (deactivateError) throw deactivateError;
      
      // Create a new subscription with the specified plan
      const now = new Date();
      const endDate = new Date(now);
      endDate.setMonth(endDate.getMonth() + 1);
      
      const { error: createError } = await supabase
        .from('subscriptions')
        .insert([{
          user_id: userId,
          plan_type: plan,
          analyses_limit: PLAN_LIMITS[plan],
          analyses_used: 0,
          start_date: now.toISOString(),
          end_date: endDate.toISOString(),
          is_active: true,
          payment_id: `admin-upgrade-${Date.now()}`,
          created_at: now.toISOString(),
          updated_at: now.toISOString()
        }]);
      
      if (createError) throw createError;
      
      // Refresh the list
      await fetchSubscriptions();
    } catch (err) {
      console.error('Error upgrading subscription:', err);
      setError('Failed to upgrade subscription');
    } finally {
      setIsUpdating(false);
    }
  };

  const resetUsageCount = async (subscriptionId: string) => {
    if (!confirm('Are you sure you want to reset the usage count for this subscription?')) return;
    
    try {
      setIsUpdating(true);
      
      const { error } = await supabase
        .from('subscriptions')
        .update({ 
          analyses_used: 0,
          updated_at: new Date().toISOString()
        })
        .eq('id', subscriptionId);
      
      if (error) throw error;
      
      // Refresh the list
      await fetchSubscriptions();
    } catch (err) {
      console.error('Error resetting usage count:', err);
      setError('Failed to reset usage count');
    } finally {
      setIsUpdating(false);
    }
  };

  // Filter subscriptions based on search term
  const filteredSubscriptions = searchTerm 
    ? subscriptions.filter(sub => 
        (sub.profiles?.email && sub.profiles.email.toLowerCase().includes(searchTerm.toLowerCase())) ||
        sub.user_id.toLowerCase().includes(searchTerm.toLowerCase()) ||
        sub.plan_type.toLowerCase().includes(searchTerm.toLowerCase())
      )
    : subscriptions;

  if (!isAdmin) {
    return (
      <div className="p-6 bg-red-50 border border-red-200 rounded-lg">
        <h3 className="text-lg font-semibold text-red-700 mb-2">Access Denied</h3>
        <p className="text-red-600">You do not have permission to access the admin dashboard.</p>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="flex justify-center items-center p-8">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-indigo-500"></div>
      </div>
    );
  }

  return (
    <div className="bg-white rounded-lg shadow-md overflow-hidden">
      <div className="bg-indigo-600 p-4 text-white">
        <h2 className="text-xl font-semibold flex items-center">
          <Crown className="mr-2 h-5 w-5" />
          Subscription Admin Dashboard
        </h2>
      </div>
      
      {error && (
        <div className="bg-red-50 border-l-4 border-red-500 p-4 m-4">
          <div className="flex">
            <AlertTriangle className="h-5 w-5 text-red-500 mr-2" />
            <p className="text-red-700">{error}</p>
          </div>
        </div>
      )}
      
      <div className="p-4">
        <div className="flex justify-between items-center mb-6">
          <div className="relative w-64">
            <span className="absolute inset-y-0 left-0 flex items-center pl-3">
              <Search className="h-5 w-5 text-gray-400" />
            </span>
            <input
              type="text"
              className="pl-10 pr-4 py-2 w-full border rounded-md focus:ring-indigo-500 focus:border-indigo-500"
              placeholder="Search subscriptions..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
            />
          </div>
          
          <button
            onClick={fetchSubscriptions}
            disabled={isUpdating}
            className="flex items-center px-4 py-2 bg-indigo-100 text-indigo-700 rounded-md hover:bg-indigo-200 transition-colors"
          >
            <RefreshCw className={`h-4 w-4 mr-2 ${isUpdating ? 'animate-spin' : ''}`} />
            Refresh
          </button>
        </div>
        
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">User</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Plan</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Status</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Usage</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Dates</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Actions</th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {filteredSubscriptions.length === 0 ? (
                <tr>
                  <td colSpan={6} className="px-6 py-4 text-center text-gray-500">
                    No subscriptions found
                  </td>
                </tr>
              ) : (
                filteredSubscriptions.map(subscription => (
                  <tr 
                    key={subscription.id}
                    className={`hover:bg-gray-50 ${!subscription.is_active ? 'bg-gray-50 text-gray-500' : ''}`}
                  >
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="flex items-center">
                        <div className="flex-shrink-0 h-10 w-10 bg-indigo-100 rounded-full flex items-center justify-center">
                          {subscription.profiles?.avatar_url ? (
                            <img 
                              src={subscription.profiles.avatar_url} 
                              alt="User" 
                              className="h-8 w-8 rounded-full"
                            />
                          ) : (
                            <User className="h-5 w-5 text-indigo-500" />
                          )}
                        </div>
                        <div className="ml-4">
                          <div className="text-sm font-medium text-gray-900">
                            {subscription.profiles?.email || subscription.user_id}
                          </div>
                          <div className="text-xs text-gray-500">
                            {subscription.profiles?.stripe_customer_id ? (
                              <span title="Stripe Customer">📊 {subscription.profiles.stripe_customer_id}</span>
                            ) : 'No Stripe ID'}
                          </div>
                        </div>
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span className={`px-2 py-1 inline-flex text-xs leading-5 font-semibold rounded-full
                        ${subscription.plan_type === SubscriptionPlan.PREMIUM ? 'bg-purple-100 text-purple-800' : 
                          subscription.plan_type === SubscriptionPlan.STANDARD ? 'bg-blue-100 text-blue-800' : 
                          'bg-gray-100 text-gray-800'}`}
                      >
                        {subscription.plan_type.toUpperCase()}
                      </span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      {subscription.is_active ? (
                        <span className="px-2 py-1 inline-flex text-xs leading-5 font-semibold rounded-full bg-green-100 text-green-800">
                          <CheckCircle className="h-4 w-4 mr-1" /> Active
                        </span>
                      ) : (
                        <span className="px-2 py-1 inline-flex text-xs leading-5 font-semibold rounded-full bg-red-100 text-red-800">
                          <AlertTriangle className="h-4 w-4 mr-1" /> Inactive
                        </span>
                      )}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="text-sm text-gray-900">
                        {subscription.analyses_used} / {subscription.analyses_limit}
                      </div>
                      <div className="w-24 h-2 bg-gray-200 rounded-full mt-1">
                        <div
                          className={`h-full rounded-full ${
                            subscription.analyses_used / subscription.analyses_limit > 0.9 ? 'bg-red-500' :
                            subscription.analyses_used / subscription.analyses_limit > 0.7 ? 'bg-yellow-500' :
                            'bg-green-500'
                          }`}
                          style={{ width: `${Math.min(100, (subscription.analyses_used / subscription.analyses_limit) * 100)}%` }}
                        ></div>
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="text-sm text-gray-900">
                        Start: {new Date(subscription.start_date).toLocaleDateString()}
                      </div>
                      {subscription.end_date && (
                        <div className="text-sm text-gray-500">
                          End: {new Date(subscription.end_date).toLocaleDateString()}
                        </div>
                      )}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                      <div className="flex space-x-2">
                        <button
                          onClick={() => subscription.id && resetUsageCount(subscription.id)}
                          disabled={isUpdating}
                          className="text-indigo-600 hover:text-indigo-900"
                          title="Reset Usage Count"
                        >
                          <RefreshCw className="h-5 w-5" />
                        </button>
                        
                        {subscription.is_active && (
                          <button
                            onClick={() => subscription.id && deactivateSubscription(subscription.id)}
                            disabled={isUpdating}
                            className="text-red-600 hover:text-red-900"
                            title="Deactivate Subscription"
                          >
                            <Trash2 className="h-5 w-5" />
                          </button>
                        )}
                        
                        <div className="relative group">
                          <button
                            className="text-green-600 hover:text-green-900"
                            title="Upgrade Plan"
                          >
                            <Crown className="h-5 w-5" />
                          </button>
                          
                          <div className="absolute right-0 mt-2 w-48 bg-white rounded-md shadow-lg py-1 z-10 hidden group-hover:block">
                            <button
                              onClick={() => {
                                if (subscription.user_id) {
                                  upgradeToPlan(subscription.user_id, SubscriptionPlan.STANDARD);
                                }
                              }}
                              className="block px-4 py-2 text-sm text-gray-700 hover:bg-gray-100 w-full text-left"
                              disabled={isUpdating}
                            >
                              Upgrade to STANDARD
                            </button>
                            <button
                              onClick={() => {
                                if (subscription.user_id) {
                                  upgradeToPlan(subscription.user_id, SubscriptionPlan.PREMIUM);
                                }
                              }}
                              className="block px-4 py-2 text-sm text-gray-700 hover:bg-gray-100 w-full text-left"
                              disabled={isUpdating}
                            >
                              Upgrade to PREMIUM
                            </button>
                          </div>
                        </div>
                      </div>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}; 